import {
  useDataSource,
  useSnackbarController,
  useTranslation,
} from "@firecms/core";
import type { Entity, EntityCollection } from "@firecms/core";
import { Button, CircularProgress } from "@firecms/ui";
import { useCallback, useRef, useState, type ChangeEvent } from "react";
import {
  AdminModal,
  adminModalFileInputClass,
} from "../custom/AdminModal";
import type { Space } from "../collections/spaces";
import {
  importLearningPath,
  learningPathImportFailures,
  learningPathImportTotals,
  prepareLearningPathImport,
  type LearningPathGraphIssue,
  type LearningPathImportSuccess,
} from "../contentImport/learningPath";
import { useAdminTranslation } from "../i18n/useAdminTranslation";
import { openSpaceFromEntity } from "./workspaceNavigationBridge";
import { buildLearningPathContentCollections } from "./buildLearningPathContentCollections";

type ImportLearningPathModalProps = {
  open: boolean;
  onClose: () => void;
  accountId: string;
  spaceCollection: EntityCollection<Space>;
  spaceId?: string;
};

function issueKey(issue: LearningPathGraphIssue, index: number): string {
  return `${issue.path}-${index}`;
}

export function ImportLearningPathModal({
  open,
  onClose,
  accountId,
  spaceCollection,
  spaceId,
}: ImportLearningPathModalProps) {
  const [loading, setLoading] = useState(false);
  const [issues, setIssues] = useState<LearningPathGraphIssue[] | null>(null);
  const [result, setResult] = useState<LearningPathImportSuccess | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dataSource = useDataSource();
  const snackbarController = useSnackbarController();
  const { t } = useTranslation();
  const { t: ta } = useAdminTranslation();
  const isUpdate = Boolean(spaceId);

  const handleClose = useCallback(() => {
    if (loading) {
      return;
    }
    setIssues(null);
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onClose();
  }, [loading, onClose]);

  const handleFile = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) {
        return;
      }

      setIssues(null);
      setResult(null);

      const prepared = prepareLearningPathImport(await file.text());
      if (!prepared.ok) {
        if ("parseError" in prepared) {
          snackbarController.open({
            message: ta("learningPathImport.parseFailed", {
              message: prepared.parseError,
            }),
            type: "warning",
          });
        } else {
          setIssues(prepared.issues);
          snackbarController.open({
            message: ta("learningPathImport.graphFailed", {
              count: prepared.issues.length,
            }),
            type: "warning",
          });
        }
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }

      try {
        setLoading(true);
        const importResult = await importLearningPath({
          dataSource,
          accountId,
          spaceCollection,
          spaceId,
          flattened: prepared.flattened,
          collectionsFor: (createdSpaceId) =>
            buildLearningPathContentCollections(accountId, createdSpaceId),
        });
        setResult(importResult);
        const totals = learningPathImportTotals(importResult);
        const snackbarKey = importResult.created
          ? "learningPathImport.snackbarCreated"
          : "learningPathImport.snackbarUpdated";
        snackbarController.open({
          message: ta(snackbarKey, {
            imported: totals.imported,
            failed: totals.failed,
          }),
          type: totals.failed > 0 ? "warning" : "success",
        });
        if (importResult.created && totals.failed === 0) {
          openSpaceFromEntity(
            {
              id: importResult.spaceId,
              path: spaceCollection.path,
              values: prepared.flattened.space,
            } as Entity<Space>,
            accountId
          );
        }
      } catch (error: unknown) {
        const message =
          error instanceof Error ? error.message : ta("common.unknownError");
        snackbarController.open({
          message: ta("learningPathImport.importFailed", { message }),
          type: "error",
        });
      } finally {
        setLoading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    },
    [
      accountId,
      dataSource,
      snackbarController,
      spaceCollection,
      spaceId,
      ta,
    ]
  );

  const failures = result ? learningPathImportFailures(result) : [];
  const totals = result ? learningPathImportTotals(result) : null;

  return (
    <AdminModal
      open={open}
      onClose={handleClose}
      ariaLabel={ta("learningPathImport.ariaLabel")}
      title={
        isUpdate
          ? ta("learningPathImport.titleUpdate")
          : ta("learningPathImport.titleCreate")
      }
      description={ta("learningPathImport.description")}
      footer={
        <>
          {loading && <CircularProgress size="small" />}
          <Button onClick={handleClose} disabled={loading} variant="outlined">
            {t("close")}
          </Button>
        </>
      }
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".json,application/json"
        disabled={loading}
        onChange={(e) => void handleFile(e)}
        className={adminModalFileInputClass}
      />

      {issues && issues.length > 0 && (
        <div className="mt-4 text-sm">
          <p className="font-medium text-slate-800 dark:text-slate-200">
            {ta("learningPathImport.graphFailed", { count: issues.length })}
          </p>
          <ul className="mt-2 max-h-48 space-y-1 overflow-auto text-slate-600 dark:text-slate-400">
            {issues.map((issue, index) => (
              <li key={issueKey(issue, index)}>
                {ta("learningPathImport.rowError", {
                  key: issue.path,
                  reason: issue.reason,
                })}
              </li>
            ))}
          </ul>
        </div>
      )}

      {totals && (
        <div className="mt-4 text-sm">
          <p className="font-medium text-slate-800 dark:text-slate-200">
            {ta("learningPathImport.summary", {
              imported: totals.imported,
              failed: totals.failed,
            })}
          </p>
          {failures.length > 0 && (
            <ul className="mt-2 max-h-48 space-y-1 overflow-auto text-slate-600 dark:text-slate-400">
              {failures.map((row) => (
                <li key={`${row.collection}-${row.index}-${row.key}`}>
                  {ta("learningPathImport.rowError", {
                    key: `${row.collection}:${row.key}`,
                    reason: row.reason,
                  })}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </AdminModal>
  );
}
